import { isPilotIdentity } from './pilotAccess';
import { API, LOCAL_STORAGE, ROUTES } from '../constants/constant';
import {
  bindSession, createBackendProfile, createTileConfiguration, createWorkflowContext,
  resolveTileProfile, sessionNamespace, TILE_IDS, TILE_ROUTES, tileForPathname,
} from './tileRouting';

// Legacy global preferences are deliberately retained but never adopted.
export const TILE_SETTINGS_KEY = 'shaka.vendor.tile-settings.v1';
export const O17 = 'odoo17-compat';
export const O19 = 'odoo19-compat';
const PROFILE_PARAM = 'backend';
const WORKFLOW_PARAM = 'workflow';
const fail = (message) => { throw new Error(message); };
const frozen = (value) => Object.freeze(value);
const parse = (storage, key) => {
  try { return JSON.parse(storage.getItem(key)); } catch { return null; }
};
const profileKey = (profile) => JSON.stringify([
  'shaka', 'vendor', 'profile-v1', profile.id, profile.backend, profile.transport,
  profile.apiBaseUrl, profile.database,
]);
const actorKey = (profile) => `actor:${profileKey(profile)}`;
const loginKey = (profile, uid) => `session:${sessionNamespace(profile, uid)}`;
const metadataFields = {
  [LOCAL_STORAGE.AUTH_TOKEN]: 'token',
  [LOCAL_STORAGE.ALLOWED_APP]: 'allowedApps',
  [LOCAL_STORAGE.TAGS]: 'tags',
  [LOCAL_STORAGE.USERNAME]: 'username',
  [LOCAL_STORAGE.VENDOR_TYPE]: 'vendorType',
  [LOCAL_STORAGE.USER_EMAIL]: 'email',
};

// Audited compatibility READ paths, not an inference from the POST verb. All
// other O19 paths (including printing/export which may create attachments) block.
export const O19_READ_PATHS = frozen([
  API.PURCHASE, API.BILLS, API.RECEIPT, API.RECEIPT_MOVE_IDS, API.DISPATCH,
  API.MANUFACTURING, API.PACKAGE, API.PACKAGE_LOTS, API.REPORT, API.MANUFACTURING_LOGS,
  API.MANUFACTURING_LOG_LINES, API.GROUP_BY, API.BATCH_LIST, API.LOT_DETAILS,
  API.PRODUCT_IMAGE, API.MIX_BOX_PACKAGE, API.BLP_LIST,
]);

export function tileSettingsKey(profiles, storage) {
  // O17 is the source identity when signed in. An O19-only sign-in has its
  // own namespace; it never impersonates or inherits an O17 actor.
  const source = [profiles[O17], profiles[O19]].find((profile) => profile && getProfileSession(profile, storage));
  if (!source) return null;
  const session = getProfileSession(source, storage);
  const targets = Object.keys(profiles).sort().map((id) => profileKey(profiles[id]));
  return JSON.stringify(['shaka.vendor.tile-settings.v2', session.namespace, targets]);
}

export function canUseO19Settings(profiles, storage = window.localStorage) {
  const source = profiles[O17] && getProfileSession(profiles[O17], storage);
  // A previously signed-in target pilot cannot grant access to a different source user.
  if (source) return isPilotIdentity(profiles[O17], source.userId);
  const target = profiles[O19] && getProfileSession(profiles[O19], storage);
  return !!target && isPilotIdentity(profiles[O19], target.userId);
}

export function configurationFromEnvironment(env, storage) {
  const profiles = {};
  // Preserve O17 as the default profile, but never infer its database from the
  // historical hardcoded staging DB. Both deployments must state the real DB.
  profiles[O17] = createBackendProfile(O17, {
    apiBaseUrl: env.REACT_APP_ODOO17_COMPAT_URL,
    database: env.REACT_APP_ODOO17_COMPAT_DB,
  });
  if (env.REACT_APP_ODOO19_COMPAT_URL || env.REACT_APP_ODOO19_COMPAT_DB) {
    profiles[O19] = createBackendProfile(O19, {
      apiBaseUrl: env.REACT_APP_ODOO19_COMPAT_URL, database: env.REACT_APP_ODOO19_COMPAT_DB,
    });
  }
  const key = tileSettingsKey(profiles, storage);
  const raw = key && canUseO19Settings(profiles, storage) ? storage.getItem(key) : null;
  let tiles = {};
  if (raw !== null) {
    try { tiles = JSON.parse(raw); } catch { fail('Saved tile settings are invalid.'); }
  }
  if (!canUseO19Settings(profiles, storage)) tiles = {};
  return createTileConfiguration({ profiles, tiles });
}

export function getProfileSession(profile, storage) {
  const uid = parse(storage, actorKey(profile));
  if (!Number.isSafeInteger(uid) || uid <= 0
      || (profile.id === O19 && !isPilotIdentity(profile, uid))) return null;
  const value = parse(storage, loginKey(profile, uid));
  if (!value || value.userId !== uid || value.namespace !== sessionNamespace(profile, uid)
      || !['server-verified', 'configured-source'].includes(value.identityProof)
      || (value.identityProof === 'configured-source' && profile.id !== O17)
      || typeof value.token !== 'string' || !value.token.trim()
      || typeof value.vendorType !== 'string' || typeof value.username !== 'string' || typeof value.email !== 'string'
      || !Array.isArray(value.allowedApps) || value.allowedApps.some((item) => typeof item !== 'string')
      || !Array.isArray(value.tags) || value.tags.some((item) => typeof item !== 'string')) return null;
  bindSession(profile, { userId: uid, token: value.token, authScheme: 'API-KEY' });
  return frozen({ ...value, allowedApps: frozen([...value.allowedApps]), tags: frozen([...value.tags]) });
}

export function saveProfileSession(profile, data, storage, email = '') {
  if (!data || !Number.isSafeInteger(data.uid) || data.uid <= 0
      || typeof data.api_key !== 'string' || !data.api_key.trim()
      || !Array.isArray(data.allowed_apps) || data.allowed_apps.some((item) => typeof item !== 'string')
      || !Array.isArray(data.tags) || data.tags.some((item) => typeof item !== 'string')) {
    fail('Login did not return verified user identity and permissions.');
  }
  // Unchanged O17 authenticates the user but does not attest DB/version.
  // Its configured source scope must never be presented as O19 identity proof.
  const legacySource = profile.id === O17 && profile.backend === 'odoo17'
    && profile.transport === 'compat'
    && !Object.prototype.hasOwnProperty.call(data, 'database')
    && !Object.prototype.hasOwnProperty.call(data, 'odoo_major');
  if (!legacySource && (data.database !== profile.database
      || data.odoo_major !== (profile.backend === 'odoo17' ? 17 : 19))) {
    fail('Login database could not be verified for this backend.');
  }
  if (profile.id === O19 && !isPilotIdentity(profile, data.uid)) {
    fail('O19 is not enabled for this account.');
  }
  const session = {
    namespace: sessionNamespace(profile, data.uid), userId: data.uid, token: data.api_key,
    identityProof: legacySource ? 'configured-source' : 'server-verified',
    allowedApps: [...data.allowed_apps], tags: [...data.tags], username: typeof data.user_name === 'string' ? data.user_name : '',
    vendorType: typeof data.vendor_type === 'string' ? data.vendor_type : '', email,
  };
  storage.setItem(loginKey(profile, data.uid), JSON.stringify(session));
  storage.setItem(actorKey(profile), JSON.stringify(data.uid));
  return getProfileSession(profile, storage);
}

export function clearProfileSession(profile, storage, expectedSession) {
  const current = getProfileSession(profile, storage);
  // A stale tab must not log out a newer login for a different actor or key.
  if (expectedSession && (!current || current.namespace !== expectedSession.namespace
      || current.token !== expectedSession.token)) return;
  if (current) storage.removeItem(loginKey(profile, current.userId));
  storage.removeItem(actorKey(profile));
}

export function tileEntryPath(tile) {
  const row = TILE_ROUTES.find((item) => item.tile === tile && !item.path.includes(':'));
  if (!row) fail('Unknown vendor tile.');
  // Dispatch's first catalog route is a sub-screen; main entry is explicit.
  return tile === 'dispatch' ? ROUTES.DISPATCH : row.path;
}

function maybeTile(pathname) {
  if ([ROUTES.HOME, ROUTES.DASHBOARD, ROUTES.LOGIN].includes(pathname)) return null;
  return tileForPathname(pathname);
}

export function createDocumentRuntime({ href, configuration, storage, newId = () => crypto.randomUUID() }) {
  const url = new URL(href);
  const tile = maybeTile(url.pathname);
  const explicit = url.searchParams.get(PROFILE_PARAM);
  if (url.searchParams.getAll(PROFILE_PARAM).length > 1 || url.searchParams.getAll(WORKFLOW_PARAM).length > 1) {
    fail('Backend/workflow selection must be unambiguous.');
  }
  const profile = url.searchParams.has(PROFILE_PARAM) ? configuration.profiles[explicit]
    : (tile ? resolveTileProfile(tile, configuration) : configuration.profiles[O17]);
  if (!profile || profile.transport !== 'compat') fail('Selected backend is unavailable for vendor tiles.');
  const session = getProfileSession(profile, storage);
  const requirePilot = () => {
    if (profile.id === O19 && !canUseO19Settings(configuration.profiles, storage)) {
      fail('O19 is not enabled for this account.');
    }
  };
  if (profile.id === O19 && url.pathname !== ROUTES.LOGIN) requirePilot();
  const workflowId = url.searchParams.has(WORKFLOW_PARAM) ? url.searchParams.get(WORKFLOW_PARAM) : newId();
  if (typeof workflowId !== 'string' || !/^[A-Za-z0-9_.:-]{1,120}$/.test(workflowId)) fail('Invalid workflow identifier.');
  const configurationForDocument = createTileConfiguration({
    profiles: configuration.profiles, tiles: tile ? { [tile]: profile.id } : {},
  });
  const workflow = tile && session ? createWorkflowContext({
    tile, configuration: configurationForDocument, userId: session.userId, workflowId,
  }) : null;
  const scope = session ? JSON.stringify([session.namespace, tile, workflowId]) : null;
  const currentSession = () => {
    const current = getProfileSession(profile, storage);
    return session && current?.namespace === session.namespace && current?.token === session.token ? session : null;
  };
  const assertLoginUnchanged = () => {
    const current = getProfileSession(profile, storage);
    if (current?.namespace !== session?.namespace || current?.token !== session?.token) {
      fail('A newer login replaced this session. Reload before signing in again.');
    }
  };
  const requireSession = () => { requirePilot(); return currentSession() || fail('This backend session expired. Sign in again.'); };
  const ownState = (state) => !state || state.__vendorScope === scope;
  const scopedState = (state) => state ? { ...state, __vendorScope: scope } : null;
  const urlFor = (destination, options = {}) => {
    const next = new URL(destination, url.origin);
    if (next.origin !== url.origin) fail('Internal navigation must stay in the vendor application.');
    const nextTile = maybeTile(next.pathname);
    if (nextTile && nextTile !== tile && !options.newWorkflow) fail('Another tile requires a new document workflow.');
    next.searchParams.set(PROFILE_PARAM, profile.id);
    next.searchParams.set(WORKFLOW_PARAM, workflowId);
    return `${next.pathname}${next.search}${next.hash}`;
  };
  const storageKey = (key) => {
    requireSession();
    // Durable UI caches are exact-backend/DB/actor/tile scoped. They never adopt
    // legacy global cache records, and a new actor cannot see the previous cache.
    return JSON.stringify(['vendor-cache-v1', session.namespace, tile, key]);
  };
  return frozen({
    profile, tile, session, workflow, workflowId, scope, href: url.href, pathname: url.pathname,
    currentSession, requireSession, assertLoginUnchanged, ownState, scopedState,
    urlFor, storageKey,
    metadata(key) {
      const current = currentSession();
      const field = metadataFields[key];
      if (!field) return undefined;
      return current ? current[field] : (['allowedApps', 'tags'].includes(field) ? [] : null);
    },
    assertRequest(path) {
      requireSession();
      if (!Object.values(API).includes(path)) fail('Unknown vendor API route.');
      if (path === API.VENDOR_LOGOUT) return;
      if (!workflow || !session.allowedApps.includes(tile)) fail('This user cannot access the selected tile.');
      if (profile.readOnly && !O19_READ_PATHS.includes(path)) {
        fail('O19 is read-only for this operation. Use its O17 tile workflow; no write-back adapter exists.');
      }
    },
  });
}

let documentRuntime;
export function getDocumentRuntime() {
  if (!documentRuntime) {
    documentRuntime = createDocumentRuntime({ href: window.location.href,
      configuration: readConfiguration(), storage: window.localStorage });
  }
  return documentRuntime;
}
export function readConfiguration() {
  return configurationFromEnvironment(process.env, window.localStorage);
}
export function saveTileSelection(tile, profileId, expectedSettingsKey) {
  const current = readConfiguration();
  if (!canUseO19Settings(current.profiles)) fail('O19 settings are not enabled for this account.');
  const next = createTileConfiguration({ profiles: current.profiles, tiles: { ...current.tiles, [tile]: profileId } });
  const key = tileSettingsKey(current.profiles, window.localStorage);
  if (!key) fail('Sign in before saving your tile preferences.');
  if (key !== expectedSettingsKey) fail('Your sign-in changed. Reload before saving tile preferences.');
  window.localStorage.setItem(key, JSON.stringify(next.tiles));
  return next;
}
export function startTileUrl(tile) {
  const config = readConfiguration();
  const profile = resolveTileProfile(tile, config);
  return `${tileEntryPath(tile)}?${PROFILE_PARAM}=${encodeURIComponent(profile.id)}&${WORKFLOW_PARAM}=${crypto.randomUUID()}`;
}
export function loginUrl(runtime = getDocumentRuntime()) {
  const next = runtime.tile ? tileEntryPath(runtime.tile) : ROUTES.DASHBOARD;
  return `${ROUTES.LOGIN}?${PROFILE_PARAM}=${encodeURIComponent(runtime.profile.id)}&next=${encodeURIComponent(next)}`;
}
export function isMetadataKey(key) { return Object.prototype.hasOwnProperty.call(metadataFields, key); }
export { TILE_IDS };
